// Plain module (not a component) so exporting these alongside DETAIL_TABS
// doesn't trip the react-refresh/only-export-components rule the way it did
// when they lived in PracticeDetailHeader.tsx.

export interface DetailTabDef {
    id: string;
    label: string;
    // Billing/Pricing/Invoices reuse the billing section's tab bodies, which
    // are gated behind utils/billingAccess.ts's hardcoded allowlist (same as
    // the standalone /billing/* routes). Consumers (PracticeDetail,
    // PracticeDetailHeader) filter DETAIL_TABS on this flag instead of
    // detailTabs.ts importing UserProvider/billingAccess itself, so this
    // stays a plain declarative module.
    requiresBillingAccess?: boolean;
}

export const DETAIL_TABS = [
    { id: 'overview', label: 'Overview', requiresBillingAccess: false },
    { id: 'details', label: 'Details', requiresBillingAccess: false },
    { id: 'patients', label: 'Patients', requiresBillingAccess: false },
    { id: 'communications', label: 'Communications', requiresBillingAccess: false },
    { id: 'users', label: 'Users', requiresBillingAccess: false },
    { id: 'billing', label: 'Billing', requiresBillingAccess: true },
    { id: 'pricing', label: 'Pricing', requiresBillingAccess: true },
    { id: 'invoices', label: 'Invoices', requiresBillingAccess: true }
] as const satisfies readonly DetailTabDef[];

export type DetailTabId = (typeof DETAIL_TABS)[number]['id'];

// Overview is the landing tab - a bare `/accounts/practices/:id` (no
// `?tab=`) lands here, per the original design's intent. Deep links to
// `?tab=details` etc. keep working via PracticeDetail's readTabFromSearch,
// which only falls back to this when the param is missing/invalid.
export const DEFAULT_TAB: DetailTabId = 'overview';

// Every id above is real and wired up. Action Items was cancelled as a
// feature and removed from DETAIL_TABS entirely (not disabled/stubbed) -
// nothing here needs a "SOON" placeholder anymore, but the enabled-list
// indirection stays so a future half-built tab can still be added disabled
// without touching the tab bar or the URL guard separately.
const ENABLED_DETAIL_TABS = [
    'overview',
    'details',
    'patients',
    'communications',
    'users',
    'billing',
    'pricing',
    'invoices'
] as const;

export function isEnabledDetailTab(value: string): value is DetailTabId {
    return (ENABLED_DETAIL_TABS as readonly string[]).includes(value);
}

// A tab is reachable when it's a real (enabled) tab AND, for the
// billing-backed ones, the current user passes hasBillingAccess. Used both
// to validate the `?tab=` URL param and to filter the rendered tab bar.
export function isTabAccessible(value: string, hasBillingAccess: boolean): value is DetailTabId {
    if (!isEnabledDetailTab(value)) return false;
    const tab = DETAIL_TABS.find((t) => t.id === value);
    return !tab?.requiresBillingAccess || hasBillingAccess;
}

// Tabs visible in the tab bar for the current user - drops the
// billing-gated tabs entirely (rather than disabling them) for users who
// fail hasBillingAccess, same as Accounts itself is hidden from the sidebar
// for users without accountsAccess.
export function getVisibleDetailTabs(hasBillingAccess: boolean): readonly DetailTabDef[] {
    return DETAIL_TABS.filter((tab) => !tab.requiresBillingAccess || hasBillingAccess);
}
